import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Typography, Box, Hidden, IconButton, FormControl, InputLabel, Select, TextField } from '@mui/material';
import { PersonAdd as IconPersonAdd, Search as IconSearch, IndeterminateCheckBox as IconIndeterminateCheckBox
    , CheckBox as IconCheckBox } from "@mui/icons-material";
import { Link } from "react-router-dom";
import * as evaluacionService from "../../services/evaluacionService";
import * as filialService from "../../services/filialService";
import { setValue } from '../../redux/evaluacionSearchSlice';
import DataGridBase from "../../components/common/DataGridBase"
import { useMantenimiento } from "../../hooks/useMantenimiento";
import DialogYesNo from '../../components/DialogYesNo';
import moment from "moment";

// Navegabilidad de evaluaciones: las evaluaciones registradas, con filtros
// por participante (texto), rango de fechas y estado.
const Index = () => {
const { isReadOnly } = useMantenimiento("evaluacion");
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const [listFiliales, setListFiliales] = useState([]);
    const filters = useSelector((state) => state.evaluacionSearch.filters);
    const [results, setResults] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [openDialogAnnulActive, setOpenDialogAnnulActive] = useState(false);
    const [selectId, setSelectId] = useState(null);
    const [selectState, setSelectState] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchResults = useCallback(async (filters) => {
        setCargando(true);

        const filialFiltro = filters.idFilial !== undefined && filters.idFilial !== 0 ? filters.idFilial : (idFilialUsuario > 0 ? idFilialUsuario : 0);
        await evaluacionService.getsEvaluacionView(0, filialFiltro)
            .then((data) => {
                // El filtro de participante es por texto: se escribe y se
                // pulsa buscar, como en el resto de navegabilidades.
                let lista = data || [];

                if (filters.member != null && filters.member.trim() !== "") {
                    lista = lista.filter((row) => (row.participante || "").toLowerCase()
                        .includes(filters.member.toLowerCase()));
                }

                if (filters.fechaInicio != null && filters.fechaInicio.trim() !== "") {
                    lista = lista.filter((row) => row.fecha >= filters.fechaInicio);
                }

                if (filters.fechaFin != null && filters.fechaFin.trim() !== "") {
                    lista = lista.filter((row) => row.fecha <= filters.fechaFin + "T23:59:59");
                }

                if (filters.idEstado > 0) {
                    lista = lista.filter((row) => row.idEstado == filters.idEstado);
                }

                setResults(lista);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
    }, [])

    useEffect(() => {
        const fetchListFiliales = async () => {
            const data = await filialService.getFilialesList().catch(() => []);
            setListFiliales(filialService.soloFilialDelUsuario(data, idFilialUsuario));
            if (idFilialUsuario > 0) {
                dispatch(setValue({ name: "idFilial", value: idFilialUsuario }));
            }
        };
        fetchListFiliales();
    }, [idFilialUsuario, dispatch]);

    useEffect(() => {
        fetchResults(filters);
    }, [fetchResults, filters]);

    const annul = async () => {
        await evaluacionService.annulEvaluacion(selectId)
            .catch((err) => {
                console.log("Err: ", err);
            });
        fetchResults(filters);
    }

    const active = async () => {
        await evaluacionService.activeEvaluacion(selectId)
            .catch((err) => {
                console.log("Err: ", err);
            });
        fetchResults(filters);
    }

    const columnas = useMemo(() => [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'filial', headerName: 'Filial', width: 75, minWidth: 65, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => p.row.codigoFilial || p.row.filial || '' },
        { field: 'participante', headerName: 'Participante', width: 220, sortable: false, headerAlign: 'center'
            , renderCell: (cellValues) => {
                return <Link to={'/evaluacion/edit/' + cellValues.row.id + '/' + cellValues.row.idParticipante}
                    style={{ color: 'inherit' }}>{cellValues.row.participante}</Link>;
            }
        },
        { field: 'fecha', headerName: 'Fecha', width: 140, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => (value && moment(value).isValid() ? moment(value).format("DD/MM/YYYY") : "")
        },
        { field: 'responsable', headerName: 'Responsable', width: 220, sortable: false, headerAlign: 'center' },
        { field: 'tipo', headerName: 'Tipo', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estado', headerName: 'Estado', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'anular', headerName: '', width: 80, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                return <IconButton aria-label="anular" size='small' style={{ height: 10 }} align={"center"} onClick={() => {
                    setSelectId(cellValues.row.id);
                    setSelectState(cellValues.row.estado);
                    setOpenDialogAnnulActive(true);
                }}>
                    { cellValues.row.estado == "A" ? <IconIndeterminateCheckBox /> : <IconCheckBox /> }
                </IconButton>;
            }
        },
    ], [])

    return (
        <div>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Evaluaciones
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Grid container item xs={12} sm={12} spacing={1}>
                        <Grid item xs={12} sm={2.5}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-filialfiltro" shrink>Filial</InputLabel>
                                <Select
                                    native
                                    label="Filial"
                                    value={filters.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                                    onChange={(event) => {
                                        dispatch(setValue({ name: "idFilial", value: parseInt(event.target.value) || 0 }));
                                    }}
                                    inputProps={{
                                        name: "idFilial",
                                        id: "input-filialfiltro",
                                    }}
                                    disabled={idFilialUsuario > 0}
                                    size="small"
                                >
                                    {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Todas las filiales)"}</option>}
                                    {listFiliales.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name="member"
                                label="Participante"
                                fullWidth
                                value={filters.member}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "member", value: event.target.value }))
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        fetchResults(filters);
                                    }
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                name="fechaInicio"
                                label="Desde"
                                type="date"
                                fullWidth
                                value={filters.fechaInicio}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "fechaInicio", value: event.target.value }))
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                name="fechaFin"
                                label="Hasta"
                                type="date"
                                fullWidth
                                value={filters.fechaFin}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "fechaFin", value: event.target.value }))
                                }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-estado" shrink>Estado</InputLabel>
                                <Select
                                    native
                                    label="Estado"
                                    value={filters.idEstado}
                                    onChange={(event) => {
                                        dispatch(setValue({ name: "idEstado", value: event.target.value }))
                                    }}
                                    inputProps={{
                                        name: 'idEstado',
                                        id: 'input-estado',
                                    }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    <option key={2} value={2}>{"Activo"}</option>
                                    <option key={4} value={4}>{"Anulado"}</option>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase
                            rows={results}
                            columns={columnas}
                            loading={cargando}
                            toolbarButtons={[
                                {
                                    text: "NUEVO",
                                    icon: <IconPersonAdd />,
                                    disabled: isReadOnly,
                                    onClick: () => navigate("/evaluacion/edit/0/0")
                                }
                            ]}
                        />
                    </Box>
                </Grid>
                <DialogYesNo open={openDialogAnnulActive} openClose={() => { setOpenDialogAnnulActive(!openDialogAnnulActive) }} setYes={ selectState == "A" ? annul : active}
                    setNo={() => { return }}
                    titulo={(selectState == "A" ? "Anular" : "Activar") + " Evaluacion"}
                    texto={"Esta seguro de " + (selectState == "A" ? "anular" : "activar") + " la evaluacion?"} >
                </DialogYesNo>
            </Grid>
        </div>
    )
}

export default Index;
